import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Grid,
} from '@mui/material';
import { formatToTimeZone } from 'date-fns-timezone';
import getInvoiceByIdAndPassword from '../../services/publicInvoice';
import { useParams } from 'react-router-dom';
import './printStyles.css';

const handlePrint = () => {
  const { scrollY } = window;

  document.body.classList.add('printing');

  window.print();

  setTimeout(() => {
    document.body.classList.remove('printing');
    window.scrollTo(0, scrollY);
  }, 100);
};

interface InvoiceData {
  saleId: number;
  totalValue: number;
  immutableData: {
    client: {
      companyName: string;
      tradingName: string;
      cnpj: string;
    };
    companyAddress: {
      street: string;
      number: string;
      complement: string;
      district: string;
      city: string;
      state: string;
      zipcode: string;
    };
    contact: {
      contactName: string;
      telephone: string;
      contactNumber: string;
      contactEmail: string;
    };
    regions: Array<{
      namespace: string;
      items: Array<{
        item: string;
        sku: string;
        qtd: number;
        unitPrice: number;
        total: number;
      }>;
    }>;
    productTier: number;
    dollarRate: number;
    dollarType: string;
    taxes?: Array<{
      isPublic: boolean;
      type: 'percentage' | 'fixed';
      value: number;
    }>;
  };
  contract: {
    seller: {
      firstName: string;
      lastName: string;
    };
  };
  createdAt: string;
  nfeNumber: string | null;
  nfeDate: string | null;
}

const PublicPartnerInvoice: React.FC = () => {
  const [cnpjDigits, setCnpjDigits] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
  const { id } = useParams<{ id: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await getInvoiceByIdAndPassword(id || '', cnpjDigits);
      if (response.success && response.data) {
        setInvoiceData(response.data);
      } else {
        setError(response.message || 'Erro ao validar CNPJ');
      }
    } catch (err) {
      setError('Erro ao processar a requisição');
    } finally {
      setLoading(false);
    }
  };

  if (invoiceData) {
    const timezone = { timeZone: 'UTC' };
    const toBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

    const taxes = invoiceData.immutableData.taxes || [];
    const hasPublicTaxes = taxes.length >= 1 && taxes.some((tax) => tax?.isPublic);

    const calculateNetPrice = (grossPrice: number): number => {
      if (!hasPublicTaxes) return grossPrice;
      const publicTaxes = invoiceData.immutableData.taxes?.filter(
        (tax) => tax.isPublic === true
      );
      if (!publicTaxes || !publicTaxes.some((tax) => tax?.isPublic)) return grossPrice;

      const taxAmount = publicTaxes
        .filter((tax) => tax.isPublic === true)
        .reduce((totalTax: number, tax) => {
          if (tax.type === 'percentage') {
            const taxPercentage = tax.value / (100 + tax.value);
            return totalTax + grossPrice * taxPercentage;
          }
          if (tax.type === 'fixed') {
            return totalTax + tax.value;
          }
          return totalTax;
        }, 0);

      return grossPrice - taxAmount;
    };

    const calculateTotalTaxes = (): number => {
      if (!hasPublicTaxes) return 0;

      return (
        invoiceData.immutableData.regions.reduce(
          (totalTaxes: number, region) =>
            totalTaxes +
            region.items.reduce((regionTaxes: number, item) => {
              const grossPrice = item.unitPrice * item.qtd;
              const netPrice = calculateNetPrice(item.unitPrice) * item.qtd;
              return regionTaxes + (grossPrice - netPrice);
            }, 0),
          0
        ) || 0
      );
    };

    // Calculate totals
    const subtotal = invoiceData.immutableData.regions.reduce(
      (pv, cv) =>
        pv +
        cv.items.reduce((pv, cv) => pv + calculateNetPrice(cv.unitPrice) * cv.qtd, 0),
      0
    );

    return (
      <Box
        sx={{ bgcolor: '#ddd', minHeight: '100vh', p: { xs: 1, md: 7.5 } }}
        className="print-container"
      >

        <Box className="no-print">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              border: '0.3px solid #e0e0e0',
              borderRadius: 1,
              maxWidth: '100%',
              mx: 'auto',
            }}
            className="print-paper"
          >
            <Grid container spacing={0} sx={{ mt: 2 }}>
              <Grid
                item
                xs={12}
                sx={{ borderBottom: '0.3px solid #ffffff', p: 1 }}
                className="print-section"
              >
                <Grid container>
                  <Grid item xs={9}>
                    <Typography variant="h6" className="print-header">
                      Pedido de venda Nº {invoiceData.saleId}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" className="print-text">
                      Chácara Santo Antonio, São Paulo - SP
                    </Typography>
                    <Typography variant="caption" display="block" className="print-text">
                      04.715-005
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={3} className="print-section">
                <Grid item xs={9}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Table size="small" className="print-table">
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={2}>Informações do Cliente</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                          >
                            Cliente
                          </TableCell>
                          <TableCell>
                            <Typography className="print-text">
                              <strong>Razão social:</strong>{' '}
                              {invoiceData.immutableData.client.companyName}
                            </Typography>
                            <Typography className="print-text">
                              <strong>Nome fantasia:</strong>{' '}
                              {invoiceData.immutableData.client.tradingName}
                            </Typography>
                            <Typography className="print-text">
                              <strong>CNPJ:</strong>{' '}
                              {invoiceData.immutableData.client.cnpj}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                          >
                            Endereço
                          </TableCell>
                          <TableCell>
                            <Typography className="print-text">
                              {`${invoiceData.immutableData.companyAddress.street}, ${
                                invoiceData.immutableData.companyAddress.number
                              }${
                                invoiceData.immutableData.companyAddress.complement
                                  ? `, ${invoiceData.immutableData.companyAddress.complement}`
                                  : ''
                              } - ${invoiceData.immutableData.companyAddress.district}`}
                            </Typography>
                            <Typography className="print-text">
                              {`${invoiceData.immutableData.companyAddress.city}, ${invoiceData.immutableData.companyAddress.state}`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                          >
                            Contato
                          </TableCell>
                          <TableCell>
                            <Typography className="print-text">
                              Fone/Cel: {invoiceData.immutableData.contact.telephone}
                            </Typography>
                            <Typography className="print-text">
                              Celular: {invoiceData.immutableData.contact.contactNumber}
                            </Typography>
                            <Typography className="print-text">
                              Email: {invoiceData.immutableData.contact.contactEmail}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                          >
                            Gerente
                          </TableCell>
                          <TableCell>
                            {invoiceData.contract.seller.firstName}{' '}
                            {invoiceData.contract.seller.lastName}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                <Grid item xs={3}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Table size="small" className="print-table">
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={2}>NF-e</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                          >
                            Pedido de Venda
                          </TableCell>
                          <TableCell>{invoiceData.saleId || '---'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                          >
                            Emissão do PV
                          </TableCell>
                          <TableCell>
                            {invoiceData.createdAt
                              ? formatToTimeZone(
                                  new Date(invoiceData.createdAt),
                                  'DD/MM/YYYY',
                                  timezone
                                )
                              : '---'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container sx={{ mt: 4 }} className="print-section">
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }} className="print-subheader">
                    Relatório de consumo
                  </Typography>
                </Grid>
                {invoiceData.immutableData.regions.map((region) => (
                  <Grid item xs={12} key={region.namespace}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                      <Table size="small" className="print-table">
                        <TableHead>
                          <TableRow>
                            <TableCell colSpan={5}>{region.namespace}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                              Item
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                              Cod.(SKU)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                              Qtd
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                              Preço Un.(R$)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                              Total (R$)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {region.items.map((row, idx) => (
                            <TableRow key={row.sku + region.namespace + idx}>
                              <TableCell sx={{ textAlign: 'left' }}>{row.item}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{row.sku}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{row.qtd}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {toBRL.format(calculateNetPrice(row.unitPrice))}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {toBRL.format(calculateNetPrice(row.unitPrice) * row.qtd)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Grid
                container
                spacing={2}
                sx={{ mt: 2 }}
                className="print-section print-section-spacing"
              >
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 'bold' }}
                    className="print-subheader"
                  >
                    Observações
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: '#f8f9fa',
                          border: '0.3px solid #e0e0e0',
                          borderRadius: 1,
                          height: '100%',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          className="print-text"
                        >
                          Tier
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold' }}
                          className="print-total"
                        >
                          {invoiceData.immutableData.productTier || '-'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: '#f8f9fa',
                          border: '0.3px solid #e0e0e0',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          className="print-text"
                        >
                          Dólar
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold' }}
                          className="print-total"
                        >
                          {invoiceData.immutableData.dollarRate?.toFixed(4) || '-'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="print-text"
                        >
                          {invoiceData.immutableData.dollarType || '-'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={4}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: '#f8f9fa',
                              border: '0.3px solid #e0e0e0',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              className="print-text"
                            >
                              SubTotal
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 'bold' }}
                              className="print-total"
                            >
                              {toBRL.format(subtotal)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={4}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: '#f8f9fa',
                              border: '0.3px solid #e0e0e0',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              className="print-text"
                            >
                              Descontos
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 'bold', color: '#000000' }}
                              className="print-total"
                            >
                              - {toBRL.format(0)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={4}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: '#f5f5f5',
                              border: '0.3px solid #e0e0e0',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              className="print-text"
                            >
                              Total
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 'bold', color: '#00c140ae' }}
                              className="print-total"
                            >
                              {toBRL.format(invoiceData.totalValue)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Box>


        <div style={{ display: 'none' }} className="print-only-layout">
          <div className="print-header-section">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px',
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: '16px' }}>
                  Pedido de venda Nº {invoiceData.saleId}
                </h2>
              </div>
              <div style={{ textAlign: 'right', fontSize: '10px' }}>
                <div>Chácara Santo Antonio, São Paulo - SP</div>
                <div>04.715-005</div>
              </div>
            </div>
            <hr style={{ margin: '5px 0' }} />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
              marginBottom: '10px',
            }}
          >

            <div
              style={{
                flex: '7',
                border: '1px solid #ddd',
                padding: '5px',
                fontSize: '9px',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th
                      colSpan={2}
                      style={{
                        textAlign: 'left',
                        borderBottom: '1px solid #ddd',
                        padding: '3px',
                      }}
                    >
                      Informações do Cliente
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px', width: '80px', verticalAlign: 'top' }}>
                      <strong>Cliente</strong>
                    </td>
                    <td style={{ padding: '3px' }}>
                      <div>
                        <strong>Razão social:</strong>{' '}
                        {invoiceData.immutableData.client.companyName}
                      </div>
                      <div>
                        <strong>Nome fantasia:</strong>{' '}
                        {invoiceData.immutableData.client.tradingName}
                      </div>
                      <div>
                        <strong>CNPJ:</strong> {invoiceData.immutableData.client.cnpj}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px', verticalAlign: 'top' }}>
                      <strong>Endereço</strong>
                    </td>
                    <td style={{ padding: '3px' }}>
                      <div>
                        {`${invoiceData.immutableData.companyAddress.street}, ${
                          invoiceData.immutableData.companyAddress.number
                        }${
                          invoiceData.immutableData.companyAddress.complement
                            ? `, ${invoiceData.immutableData.companyAddress.complement}`
                            : ''
                        } - ${invoiceData.immutableData.companyAddress.district}`}
                      </div>
                      <div>
                        {`${invoiceData.immutableData.companyAddress.city}, ${invoiceData.immutableData.companyAddress.state}`}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px', verticalAlign: 'top' }}>
                      <strong>Contato</strong>
                    </td>
                    <td style={{ padding: '3px' }}>
                      <div>Fone/Cel: {invoiceData.immutableData.contact.telephone}</div>
                      <div>
                        Celular: {invoiceData.immutableData.contact.contactNumber}
                      </div>
                      <div>Email: {invoiceData.immutableData.contact.contactEmail}</div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px', verticalAlign: 'top' }}>
                      <strong>Gerente</strong>
                    </td>
                    <td style={{ padding: '3px' }}>
                      {invoiceData.contract.seller.firstName}{' '}
                      {invoiceData.contract.seller.lastName}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>


            <div
              style={{
                flex: '3',
                border: '1px solid #ddd',
                padding: '5px',
                fontSize: '9px',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th
                      colSpan={2}
                      style={{
                        textAlign: 'left',
                        borderBottom: '1px solid #ddd',
                        padding: '3px',
                      }}
                    >
                      NF-e
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px', width: '50%' }}>
                      <strong>Pedido de Venda</strong>
                    </td>
                    <td style={{ padding: '3px' }}>{invoiceData.saleId || '---'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px' }}>
                      <strong>Emissão do PV</strong>
                    </td>
                    <td style={{ padding: '3px' }}>
                      {invoiceData.createdAt
                        ? formatToTimeZone(
                            new Date(invoiceData.createdAt),
                            'DD/MM/YYYY',
                            timezone
                          )
                        : '---'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>


          <div style={{ marginBottom: '10px' }}>
            <h3 style={{ fontSize: '12px', margin: '5px 0' }}>Relatório de consumo</h3>

            {invoiceData.immutableData.regions.map((region, regionIndex) => (
              <div
                key={`${region.namespace}-${regionIndex}`}
                style={{ border: '1px solid #ddd', marginBottom: '5px' }}
              >
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}
                >
                  <thead>
                    <tr>
                      <th
                        colSpan={5}
                        style={{
                          textAlign: 'left',
                          borderBottom: '1px solid #ddd',
                          padding: '3px',
                          background: '#f5f5f5',
                        }}
                      >
                        {region.namespace}
                      </th>
                    </tr>
                    <tr style={{ background: '#f9f9f9' }}>
                      <th
                        style={{
                          padding: '3px',
                          textAlign: 'left',
                          borderBottom: '1px solid #ddd',
                          fontWeight: 'bold',
                        }}
                      >
                        Item
                      </th>
                      <th
                        style={{
                          padding: '3px',
                          textAlign: 'left',
                          borderBottom: '1px solid #ddd',
                          fontWeight: 'bold',
                        }}
                      >
                        Cod.(SKU)
                      </th>
                      <th
                        style={{
                          padding: '3px',
                          textAlign: 'left',
                          borderBottom: '1px solid #ddd',
                          fontWeight: 'bold',
                        }}
                      >
                        Qtd
                      </th>
                      <th
                        style={{
                          padding: '3px',
                          textAlign: 'right',
                          borderBottom: '1px solid #ddd',
                          fontWeight: 'bold',
                        }}
                      >
                        Preço Un.(R$)
                      </th>
                      <th
                        style={{
                          padding: '3px',
                          textAlign: 'right',
                          borderBottom: '1px solid #ddd',
                          fontWeight: 'bold',
                        }}
                      >
                        Total (R$)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {region.items.map((item, itemIndex) => (
                      <tr key={`${item.sku}-${itemIndex}`}>
                        <td style={{ padding: '3px', borderBottom: '1px solid #eee' }}>
                          {item.item}
                        </td>
                        <td style={{ padding: '3px', borderBottom: '1px solid #eee' }}>
                          {item.sku}
                        </td>
                        <td style={{ padding: '3px', borderBottom: '1px solid #eee' }}>
                          {item.qtd}
                        </td>
                        <td
                          style={{
                            padding: '3px',
                            textAlign: 'right',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {toBRL.format(calculateNetPrice(item.unitPrice))}
                        </td>
                        <td
                          style={{
                            padding: '3px',
                            textAlign: 'right',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {toBRL.format(calculateNetPrice(item.unitPrice) * item.qtd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <div>
            <h3 style={{ fontSize: '12px', margin: '5px 0' }}>Observações</h3>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div
                style={{
                  flex: '1',
                  border: '1px solid #ddd',
                  padding: '5px',
                  background: '#f8f9fa',
                  fontSize: '9px',
                }}
              >
                <div style={{ color: '#666', marginBottom: '2px' }}>Tier</div>
                <div style={{ fontWeight: 'bold', fontSize: '11px' }}>
                  {invoiceData.immutableData.productTier || '-'}
                </div>
              </div>

              <div
                style={{
                  flex: '1',
                  border: '1px solid #ddd',
                  padding: '5px',
                  background: '#f8f9fa',
                  fontSize: '9px',
                }}
              >
                <div style={{ color: '#666', marginBottom: '2px' }}>Dólar</div>
                <div style={{ fontWeight: 'bold', fontSize: '11px' }}>
                  {invoiceData.immutableData.dollarRate?.toFixed(4) || '-'}
                </div>
                <div style={{ color: '#666', fontSize: '8px' }}>
                  {invoiceData.immutableData.dollarType || '-'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div
                style={{
                  flex: '1',
                  border: '1px solid #ddd',
                  padding: '5px',
                  background: '#f8f9fa',
                  fontSize: '9px',
                }}
              >
                <div style={{ color: '#666', marginBottom: '2px' }}>SubTotal</div>
                <div style={{ fontWeight: 'bold', fontSize: '11px' }}>
                  {toBRL.format(subtotal)}
                </div>
              </div>

              <div
                style={{
                  flex: '1',
                  border: '1px solid #ddd',
                  padding: '5px',
                  background: '#f8f9fa',
                  fontSize: '9px',
                }}
              >
                <div style={{ color: '#666', marginBottom: '2px' }}>Descontos</div>
                <div style={{ fontWeight: 'bold', fontSize: '11px' }}>
                  - {toBRL.format(0)}
                </div>
              </div>

              {hasPublicTaxes && (
                <div
                  style={{
                    flex: '1',
                    border: '1px solid #ddd',
                    padding: '5px',
                    background: '#f8f9fa',
                    fontSize: '9px',
                  }}
                >
                  <div style={{ color: '#666', marginBottom: '2px' }}>Impostos</div>
                  <div style={{ fontWeight: 'bold', fontSize: '11px', color: '#d32f2f' }}>
                    - {toBRL.format(calculateTotalTaxes())}
                  </div>
                </div>
              )}

              <div
                style={{
                  flex: '1',
                  border: '1px solid #ddd',
                  padding: '5px',
                  background: '#f5f5f5',
                  fontSize: '9px',
                }}
              >
                <div style={{ color: '#666', marginBottom: '2px' }}>Total</div>
                <div style={{ fontWeight: 'bold', fontSize: '11px', color: '#00c140ae' }}>
                  {toBRL.format(invoiceData.totalValue)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Box sx={{ mt: 3, textAlign: 'center' }} className="no-print">
          <Button variant="contained" color="primary" onClick={handlePrint}>
            Imprimir Fatura
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 2,
        bgcolor: '#ddd',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 400,
          width: '100%',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Validação de CNPJ
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Digite os 4 primeiros dígitos do seu CNPJ"
            variant="outlined"
            value={cnpjDigits}
            onChange={(e) => setCnpjDigits(e.target.value)}
            inputProps={{
              maxLength: 4,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            sx={{ mb: 2 }}
            disabled={loading}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading || cnpjDigits.length !== 4}
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default PublicPartnerInvoice;
